/**
 * viewportState.js — Shared state & core helpers for viewport modules
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.0 — ROBUST STATE MANAGEMENT]:
 *  - destroyRenderingEngine(): обнуление после destroy
 *  - resetGlobalAC(): пересоздание AbortController
 *  - hasViewport(id), getViewportCount(), getViewportType(id)
 *  - destroyViewportState(): полная очистка
 *  - Новые раскладки: 2x1, 1x3, 2x3, 3x3
 *  - Viewport layout presets (cardiac, chest, brain, vascular)
 *  - Viewport maximized mode (maximizeViewport, restoreViewport)
 *  - find3DViewportId(): fallback null вместо хардкода
 *
 * Все viewport-модули импортируют state и helpers отсюда.
 * Единственный источник истины для _renderingEngine, _viewports, _cinematicRenderer.
 */

import { cornerstone, cornerstoneTools } from './init.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════════

export const RENDERING_ENGINE_ID = 'mikhmelRenderingEngine';
export const TOOL_GROUP_ID = 'mikhmelToolGroup';
export const DEFAULT_3D_PRESET = 'ct-bone-clean';
export const DEFAULT_3D_VIEW = 'superior';

export const { RenderingEngine, Enums: { ViewportType, OrientationAxis }, CONSTANTS } = cornerstone;
export { cornerstone, cornerstoneTools };

// ══════════════════════════════════════════════════════════════
//  LAYOUTS (расширенные)
// ══════════════════════════════════════════════════════════════

export const LAYOUTS = Object.freeze({
  '1x1': { rows: 1, cols: 1, viewports: ['main'] },
  '1x2': { rows: 1, cols: 2, viewports: ['left', 'right'] },
  '1x3': { rows: 1, cols: 3, viewports: ['left', 'center', 'right'] },
  '2x1': { rows: 2, cols: 1, viewports: ['top', 'bottom'] },
  '2x2': { rows: 2, cols: 2, viewports: ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] },
  '2x3': { rows: 2, cols: 3, viewports: ['tl', 'tc', 'tr', 'bl', 'bc', 'br'] },
  '3x3': { rows: 3, cols: 3, viewports: ['v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8', 'v9'] },
  'mpr':  { rows: 2, cols: 2, viewports: ['axial', 'sagittal', 'coronal', 'native'] },
  // [10.0.2] Шесть окон: те же четыре проекции плюс два объёмных.
  // Второе объёмное отличается автоматически применённым пресетом (кость для
  // КТ, мозг для МР) — чтобы рядом были и «как снято», и готовая укладка,
  // без переключения пресета туда-сюда.
  'mpr6': { rows: 2, cols: 3, viewports: ['native', 'axial', 'sagittal', 'coronal', 'volume3d', 'view-3d'] },
});

/**
 * [10.0.2] Раскладки, которые строят ОБЪЁМ и раскладывают его по проекциям.
 *
 * Список живёт рядом с LAYOUTS, а не в layoutManager, потому что спрашивают
 * его трое: layoutManager (какой режим ставить), uiManager (каким путём
 * грузить серию) и viewport (какие типы окон создавать). Пока признак был
 * записан в каждом отдельным сравнением с 'mpr', добавление mpr6 означало три
 * правки в трёх файлах — и одну из них (режим в changeLayout) легко было
 * пропустить: пять окон из шести просто остались бы чёрными.
 */
export const MPR_LAYOUTS = Object.freeze(['mpr', 'mpr6']);

/** Строит ли раскладка объём (реформаты), а не показывает исходную стопку. */
export function isMprLayout(layout) { return MPR_LAYOUTS.includes(layout); }

/** Режим просмотра, соответствующий раскладке. */
export function layoutMode(layout) { return isMprLayout(layout) ? 'mpr' : '2d'; }

/**
 * [10.0.2] Где стоит общий ползунок листания — в промежутке между первой и
 * второй колонкой сетки.
 *
 * Раньше координата была вшита в CSS правилом [data-cols="2"] → left:50%.
 * Для трёх колонок (раскладка mpr6) правило не срабатывало вовсе, и ползунок
 * уезжал к правому краю экрана — из отведённого ему зазора в чужое место.
 *
 * Колонка шириной (100% − (N−1)·G)/N, плюс половина зазора — это ровно центр
 * первого промежутка. Для двух колонок выражение даёт 50%, то есть прежнее
 * поведение сохраняется без изменения на пиксель.
 */
export function sliderXExpr(cols, gap) {
  if (!(cols > 1) || !(gap > 0)) return null;
  return `calc((100% - ${(cols - 1) * gap}px) / ${cols} + ${gap / 2}px)`;
}

/** То же в пикселях — для проверок и отладки. */
export function sliderXPx(cols, gap, widthPx) {
  if (!(cols > 1) || !(gap > 0) || !(widthPx > 0)) return null;
  return (widthPx - (cols - 1) * gap) / cols + gap / 2;
}

export const VP_TITLES = Object.freeze({
  axial: 'Axial', sagittal: 'Sagittal', coronal: 'Coronal',
  native: 'Нативные срезы',
  volume3d: '3D Volume', 'view-3d': '3D · пресет',
  main: 'Main View', left: 'Left', right: 'Right', center: 'Center',
  top: 'Top', bottom: 'Bottom',
  topLeft: 'TL', topRight: 'TR', bottomLeft: 'BL', bottomRight: 'BR',
  tl: 'TL', tc: 'TC', tr: 'TR', bl: 'BL', bc: 'BC', br: 'BR',
  v1: '1', v2: '2', v3: '3', v4: '4', v5: '5', v6: '6', v7: '7', v8: '8', v9: '9',
});

// ══════════════════════════════════════════════════════════════
//  LAYOUT PRESETS (именованные конфигурации)
// ══════════════════════════════════════════════════════════════

export const LAYOUT_PRESETS = Object.freeze({
  'cardiac': {
    layout: '2x2',
    viewportConfig: {
      topLeft: { type: 'axial', label: 'Axial (4-chamber)' },
      topRight: { type: 'coronal', label: 'Coronal' },
      bottomLeft: { type: 'sagittal', label: 'Sagittal' },
      bottomRight: { type: 'volume3d', label: '3D Volume', preset: 'cardiac-3d' },
    },
  },
  'chest': {
    layout: 'mpr',
    viewportConfig: {
      axial: { type: 'axial', label: 'Axial (Lung)', wwwc: { ww: 1500, wc: -600 } },
      sagittal: { type: 'sagittal', label: 'Sagittal' },
      coronal: { type: 'coronal', label: 'Coronal' },
      native: { type: 'stack', label: 'Нативные срезы', wwwc: { ww: 1500, wc: -600 } },
    },
  },
  'brain': {
    layout: '1x3',
    viewportConfig: {
      left: { type: 'axial', label: 'Axial', wwwc: { ww: 80, wc: 40 } },
      center: { type: 'sagittal', label: 'Sagittal', wwwc: { ww: 80, wc: 40 } },
      right: { type: 'coronal', label: 'Coronal', wwwc: { ww: 80, wc: 40 } },
    },
  },
  'vascular': {
    layout: '1x2',
    viewportConfig: {
      left: { type: 'coronal', label: 'MIP Coronal', slab: { mode: 'mip', thicknessMm: 20 } },
      right: { type: 'volume3d', label: '3D Anglo', preset: 'ct-vascular' },
    },
  },
  'compare': {
    layout: '2x1',
    viewportConfig: {
      top: { type: 'stack', label: 'Current' },
      bottom: { type: 'stack', label: 'Prior' },
    },
  },
});

// ══════════════════════════════════════════════════════════════
//  MUTABLE STATE (singleton)
// ══════════════════════════════════════════════════════════════

const state = {
  /** @type {import('@cornerstonejs/core').RenderingEngine|null} */
  renderingEngine: null,
  /** @type {Map<string, {element: HTMLElement, type: string, orientation?: any}>} */
  viewports: new Map(),
  /** @type {Object|null} */
  cinematicRenderer: null,
  /** @type {{abort: () => void}|null} */
  volumeAbortController: null,
  /** @type {AbortController} Global event cleanup */
  globalAC: new AbortController(),
  /** @type {string|null} Maximized viewport ID */
  maximizedViewportId: null,
  /** @type {Object|null} Saved layout before maximize */
  _preMaximizeLayout: null,
};

export default state;

// ══════════════════════════════════════════════════════════════
//  CORE HELPERS
// ══════════════════════════════════════════════════════════════

export function getRenderingEngine() {
  if (!state.renderingEngine) {
    state.renderingEngine = new RenderingEngine(RENDERING_ENGINE_ID);
  }
  return state.renderingEngine;
}

/**
 * [9.0.0] Установка окна (W/L) переехала сюда из viewport.js.
 *
 * Причина — круг: viewport.js импортировал viewportEffects.js, а тот брал
 * setWindowLevel обратно из viewport.js. Сборке это не мешало и работало
 * случайно: функция вызывалась только внутри другой функции, к тому моменту
 * круг успевал замкнуться. Стоило позвать её на верхнем уровне модуля — и
 * получили бы undefined без единого предупреждения.
 *
 * Здесь ей и место: всё, что нужно, — движок рендеринга и сам cornerstone,
 * и то и другое живёт в этом файле. Круга больше нет.
 */
export function setWindowLevel(viewportId, windowWidth, windowCenter) {
  const vp = getRenderingEngine().getViewport(viewportId);
  if (!vp) return;
  const { lower, upper } = cornerstone.utilities.windowLevel.toLowHighRange(windowWidth, windowCenter);
  vp.setProperties({ voiRange: { lower, upper } });
  vp.render();
}

/** Уничтожить rendering engine и обнулить ссылку */
export function destroyRenderingEngine() {
  if (state.renderingEngine) {
    try { state.renderingEngine.destroy(); } catch (e) { log.debug('[viewportState] destroy:', e.message); }
    state.renderingEngine = null;
  }
}

/** Получить все viewport IDs */
export function getViewportIds() {
  return [...state.viewports.keys()];
}

/** Проверить существование viewport */
export function hasViewport(viewportId) {
  return state.viewports.has(viewportId);
}

/** Количество активных viewport'ов */
export function getViewportCount() {
  return state.viewports.size;
}

/** Получить элемент viewport'а */
export function getViewportElement(viewportId) {
  return state.viewports.get(viewportId)?.element || null;
}

/** Получить тип viewport'а */
export function getViewportType(viewportId) {
  return state.viewports.get(viewportId)?.type || null;
}

/** Найти 3D viewport ID (возвращает null если не найден) */
export function find3DViewportId() {
  const re = getRenderingEngine();
  const vps = re?.getViewports();
  if (vps) {
    for (const vp of vps) {
      if (vp.type === ViewportType.VOLUME_3D) return vp.id;
    }
  }
  // Проверяем state.viewports на случай если движок ещё не создан
  for (const [id, entry] of state.viewports) {
    if (entry.type === ViewportType.VOLUME_3D || entry.type === 'volume3d') return id;
  }
  return null;
}

/**
 * [10.0.2] ВСЕ объёмные окна, а не первое попавшееся.
 *
 * find3DViewportId() возвращает первое и годится, пока объёмное окно одно.
 * В раскладке mpr6 их два, и loadVolume, собирая список окон как
 * [...viewportIds, find3DViewportId()], привязал бы том только к одному —
 * второе осталось бы чёрным.
 */
export function find3DViewportIds() {
  const out = [];
  const re = getRenderingEngine();
  const vps = re?.getViewports();
  if (vps) {
    for (const vp of vps) if (vp.type === ViewportType.VOLUME_3D) out.push(vp.id);
  }
  if (!out.length) {
    for (const [id, entry] of state.viewports) {
      if (entry.type === ViewportType.VOLUME_3D || entry.type === 'volume3d') out.push(id);
    }
  }
  return out;
}

// ══════════════════════════════════════════════════════════════
//  ACTIVE VIEWPORT TRACKING
// ══════════════════════════════════════════════════════════════

let _hoveredViewportId = null;
let _activeViewportId = null;

export function getActiveOrFirstViewportId() {
  if (_activeViewportId && state.viewports.has(_activeViewportId)) return _activeViewportId;
  if (_hoveredViewportId && state.viewports.has(_hoveredViewportId)) return _hoveredViewportId;
  const ids = getViewportIds();
  return ids.length ? ids[0] : null;
}

export function setHoveredViewportId(vpId) { _hoveredViewportId = vpId; }

export function setActiveViewportId(vpId) {
  if (vpId && state.viewports.has(vpId)) {
    _activeViewportId = vpId;
  }
}

export function getActiveViewportId() {
  return _activeViewportId;
}

/**
 * [12.0.49] Окно снимка, выбранное щелчком в интерфейсе (uiManager держит его
 * у себя: _currentViewportId). Ядро о нём не знало — _activeViewportId здесь
 * не задаёт никто, и «активным» для ядра было окно под мышью или первое.
 * Для окна W/L из правой панели это не годится: мышь в этот миг над панелью,
 * а не над снимком. uiManager регистрирует источник один раз при запуске.
 */
let _источникТекущего = null;
export function задатьИсточникТекущегоОкна(fn) { _источникТекущего = typeof fn === 'function' ? fn : null; }

/** Окно, выбранное в интерфейсе; если его нет — активное, под мышью или первое. */
export function текущееОкноСнимка() {
  try {
    const id = _источникТекущего?.();
    if (id && state.viewports.has(id)) return id;
  } catch (e) { /* источник ещё не готов */ }
  return getActiveOrFirstViewportId();
}

// ══════════════════════════════════════════════════════════════
//  CINEMATIC RENDERER
// ══════════════════════════════════════════════════════════════

export function getCinematicRenderer() { return state.cinematicRenderer; }
export function setCinematicRenderer(renderer) { state.cinematicRenderer = renderer; }

// ══════════════════════════════════════════════════════════════
//  VIEWPORT MAXIMIZE / RESTORE
// ══════════════════════════════════════════════════════════════

/**
 * Развернуть один viewport на весь экран.
 * @param {string} viewportId
 * @returns {boolean}
 */
export function maximizeViewport(viewportId) {
  if (!state.viewports.has(viewportId)) return false;
  if (state.maximizedViewportId === viewportId) return true; // уже развёрнут

  // Сохраняем текущее состояние
  if (!state.maximizedViewportId) {
    state._preMaximizeLayout = {
      viewports: new Map(state.viewports),
      gridStyle: {},
    };
    // Сохраняем стили контейнера
    const container = document.getElementById('viewport-container');
    if (container) {
      state._preMaximizeLayout.gridStyle = {
        gridTemplateRows: container.style.gridTemplateRows,
        gridTemplateColumns: container.style.gridTemplateColumns,
        gap: container.style.gap,
        display: container.style.display,
      };
    }
  }

  state.maximizedViewportId = viewportId;

  // Скрываем все кроме целевого
  const container = document.getElementById('viewport-container');
  if (container) {
    container.style.gridTemplateRows = '1fr';
    container.style.gridTemplateColumns = '1fr';
    container.style.gap = '0';
  }

  for (const [id, entry] of state.viewports) {
    if (entry.element) {
      entry.element.style.display = id === viewportId ? '' : 'none';
    }
  }

  // Ресайз движка
  try {
    const re = getRenderingEngine();
    if (re) re.resize(true, true);
  } catch (e) { log.debug('[viewportState] maximize:', e.message); }

  log.debug(`[Viewport] Maximized: ${viewportId}`);
  return true;
}

/**
 * Восстановить раскладку после maximize.
 */
export function restoreViewportLayout() {
  if (!state.maximizedViewportId) return false;

  const container = document.getElementById('viewport-container');
  if (container && state._preMaximizeLayout) {
    const gl = state._preMaximizeLayout.gridStyle;
    container.style.gridTemplateRows = gl.gridTemplateRows || '';
    container.style.gridTemplateColumns = gl.gridTemplateColumns || '';
    container.style.gap = gl.gap || '';
    container.style.display = gl.display || '';
  }

  for (const [id, entry] of state.viewports) {
    if (entry.element) {
      entry.element.style.display = '';
    }
  }

  state.maximizedViewportId = null;
  state._preMaximizeLayout = null;

  try {
    const re = getRenderingEngine();
    if (re) re.resize(true, true);
  } catch (e) { log.debug('[viewportState] restore:', e.message); }

  log.debug('[Viewport] Layout restored');
  return true;
}

export function isViewportMaximized() {
  return state.maximizedViewportId !== null;
}

export function getMaximizedViewportId() {
  return state.maximizedViewportId;
}

// ══════════════════════════════════════════════════════════════
//  GLOBAL ABORT CONTROLLER
// ══════════════════════════════════════════════════════════════

export function resetGlobalAC() {
  try { state.globalAC.abort(); } catch (e) { /* уже aborted */ }
  state.globalAC = new AbortController();
}

// ══════════════════════════════════════════════════════════════
//  DESTROY
// ══════════════════════════════════════════════════════════════

export function destroyViewportState() {
  // Очищаем viewports
  for (const [id, entry] of state.viewports) {
    try {
      const re = getRenderingEngine();
      if (re && entry.element) re.disableElement(entry.element);
    } catch (e) { log.debug('[viewportState] disable:', e.message); }
  }
  state.viewports.clear();

  // Уничтожаем движок
  destroyRenderingEngine();

  // Очищаем cinematic
  if (state.cinematicRenderer) {
    try { state.cinematicRenderer.dispose?.(); } catch (e) { /* */ }
    state.cinematicRenderer = null;
  }

  // Отменяем загрузку
  if (state.volumeAbortController) {
    try { state.volumeAbortController.abort(); } catch (e) { /* */ }
    state.volumeAbortController = null;
  }

  // Сбрасываем глобальный AC
  resetGlobalAC();

  // Сбрасываем ховер/актив
  _hoveredViewportId = null;
  _activeViewportId = null;

  // Сбрасываем maximize
  state.maximizedViewportId = null;
  state._preMaximizeLayout = null;

  log.debug('[ViewportState] Destroyed');
}